import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Typography, FormGroup, TextField, InputAdornment, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useOptionsService } from '#service/autocomplete-service/hooks';
import MobileFilterCheckbox from '#component/general/list-filter/components/general/checkbox-group/mobile-filter-checkbox';
import ArrowRightButton from '#component/general/arrow-right-button/arrow-right-button';
import PreloaderComponent from '#component/preloader';
import {
  SubComponentContainer,
  ShipmentArrowButton,
  FullScreenOverlay,
  OverlayContent,
  MobileSelectorTitle,
  SelectorCloseButton,
  SelectorTitleText,
  FilterSearchArea,
  FilterSearchInfo,
  MobileFilterApplyBtn,
  MobileFilterPreloader,
  StyledChip,
  FilterDivider
} from '#component/general/list-filter/components/mobile/styles';
import LargeFixedButton from '#component/general/large-fixed-button';
import { useOverlay } from '#component/general/list-filter/hooks/useOverlayContext';

export const MultipleSelect = ({
  name,
  label,
  mobileLabel,
  relatedTo,
  onChange,
  searchFields,
  fetchOptionsService
}) => {
  const { options, status, fetchOptions, clearOptions } =
    useOptionsService(searchFields, fetchOptionsService);
  const { setIsOverlayOpen } = useOverlay();
  const loading = status === 'loading' && options.length === 0;
  const selected = useSelector(s => s.listFilter?.[relatedTo]?.[name]?.value, shallowEqual) ?? [];
  const [value, setValue] = useState(selected);
  const [tempValue, setTempValue] = useState(selected);
  const [inputValue, setInputValue] = useState('');
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [inSelectionMode, setInSelectionMode] = useState(false);

  useEffect(() => {
    if (overlayOpen) {
      setIsOverlayOpen(true); // Notify the context
      fetchOptions(); // Fetch options when the overlay is opened
    } else {
      setIsOverlayOpen(false); // Notify the context when the overlay closes
    }
  }, [overlayOpen, setIsOverlayOpen, fetchOptions]);

  const handleOpenOverlay = () => {
    setTempValue(value); // Reset tempValue to the saved state
    setOverlayOpen(true);
    setInSelectionMode(true);
  };

  const handleCloseOverlay = () => {
    if (inSelectionMode) {
      setInSelectionMode(false);
      setTimeout(() => {
        setOverlayOpen(false);
        clearOptions();
      }, 300);
    } else {
      setOverlayOpen(false);
      clearOptions();
    }
  };

  const handleToggle = option => {
    setTempValue(prev => {
      const isSelected = prev.find(item => item.label === option.label);
      if (isSelected) {
        return prev.filter(item => item.label !== option.label);
      }
      return [...prev, option];
    });
  };

  const handleChange = e => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);

    if (newInputValue === '') {
      fetchOptions();
    }
  };

  const handleSearchOnEnter = e => {
    if (e.key === 'Enter' && inputValue.length >= 2) {
      fetchOptions(inputValue);
    }
  };

  const handleRemoveSelection = optionToRemove => {
    const newValue = value.filter(option => option.label !== optionToRemove.label);
    setValue(newValue);
    onChange(newValue);
  };

  const handleDeselectAll = () => {
    setTempValue([]);
  };

  const handleApply = () => {
    setValue(tempValue);
    onChange(tempValue);
    handleCloseOverlay();
  };

  if (loading) {
    return (
      <MobileFilterPreloader>
        <PreloaderComponent />
      </MobileFilterPreloader>
    );
  }

  return (
    <>
      <SubComponentContainer>
        <ShipmentArrowButton>
          <Typography color="textSecondary" fontWeight={500}>
            {mobileLabel || label}
          </Typography>
          <ArrowRightButton onClickArrowRight={handleOpenOverlay} />
        </ShipmentArrowButton>

        {overlayOpen && (
          <FullScreenOverlay>
            <OverlayContent>
              <MobileSelectorTitle>
                <SelectorCloseButton
                  edge="start"
                  color="inherit"
                  onClick={handleCloseOverlay}
                  aria-label="back"
                >
                  <ChevronLeftIcon />
                </SelectorCloseButton>
                <SelectorTitleText fontWeight={500}>
                  Choose
                  {' '}
                  {label.toLowerCase()}
                </SelectorTitleText>
              </MobileSelectorTitle>
              <FilterSearchArea>
                <TextField
                  label={`Search ${label.toLowerCase()}`}
                  variant="outlined"
                  fullWidth
                  type="search"
                  value={inputValue}
                  onChange={handleChange}
                  onKeyDown={handleSearchOnEnter}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <FilterSearchInfo>
                  <Typography variant="title" color="textSecondary" fontWeight={500}>
                    Selected (
                    {tempValue.length}
                    )
                  </Typography>
                  <Button onClick={handleDeselectAll}>Deselect All</Button>
                </FilterSearchInfo>
                <FormGroup>
                  {options.map(option => (
                    <MobileFilterCheckbox
                      key={option.label}
                      label={option.label}
                      selected={!!tempValue.find(v => v.label === option.label)}
                      onToggle={() => handleToggle(option)}
                    />
                  ))}
                </FormGroup>
              </FilterSearchArea>
              <MobileFilterApplyBtn>
                <LargeFixedButton onClick={handleApply} buttonName="Apply" />
              </MobileFilterApplyBtn>
            </OverlayContent>
          </FullScreenOverlay>
        )}
      </SubComponentContainer>
      <div>
        {value.map(option => (
          <StyledChip
            key={option.label}
            label={option.label}
            onDelete={() => handleRemoveSelection(option)}
            deleteIcon={<CloseIcon />}
          />
        ))}
      </div>
      <FilterDivider />
    </>
  );
};
